import React, { useEffect, useMemo, useRef } from 'react';
import { ResourceType } from '@cotera/api';
import { DG } from '@cotera/sdk/core';
import { useWatchableValue } from '@cotera/client/v0/actions/framework';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { resourceArtifactData } from '@cotera/client/v0/chat/artifacts';
import { useAgentModel } from './agent.model';

/**
 * Renders nothing. When mounted for the chat's **top-level** agent, it surfaces the
 * documents that agent `@`-mentions in its prompt as sibling artifact tabs in the
 * chat's artifact panel — so they're viewable in-place instead of navigating away
 * to a full-screen page. Mentioned agents are opened only when their resource pill
 * is clicked.
 *
 * - **Documents only** (per product), never agents.
 * - **Re-derived from the prompt** on every render, so the tabs survive reload and
 *   reflect edits without anything being persisted. Removing a mention closes the
 *   tab this expander opened for it.
 * - **Background tabs** (`focus: false`): they land in the dropdown without pulling the
 *   user off the agent they're viewing.
 * - **Top-level only**: sub-agents opened as their own tabs aren't rendered with this
 *   expander, so their references aren't expanded in turn (only the first-level
 *   agent's `@`-mentions surface).
 */
export const AgentReferenceArtifactsExpander: React.FC = () => {
  const model = useAgentModel();
  const editorState = useWatchableValue(model.editorStateWatchable);
  const artifactPanel = useArtifactPanel();
  const selfAgentId = model.agentId;
  // Reference tabs this expander has opened, so it can close the ones whose mention
  // is later removed from the prompt (without touching user- or tool-opened tabs).
  const managedArtifactIdsRef = useRef<Set<string>>(new Set());

  // A stable key of the referenced resources so the effect only re-runs when the set
  // of mentions changes, not on every keystroke in the prompt.
  const referenceKey = useMemo(() => {
    return DG.collectWorkspaceResourceRefs(editorState)
      .filter(
        (ref) =>
          ref.resourceType === ResourceType.Document &&
          ref.resourceId !== selfAgentId
      )
      .map((ref) => `${ref.resourceType}:${ref.resourceId}`)
      .join('\n');
  }, [editorState, selfAgentId]);

  useEffect(() => {
    if (artifactPanel === null) {
      return;
    }
    const previousIds = managedArtifactIdsRef.current;
    const currentIds = new Set<string>();
    if (referenceKey !== '') {
      for (const entry of referenceKey.split('\n')) {
        const separator = entry.indexOf(':');
        const resourceType = entry.slice(0, separator) as ResourceType;
        const resourceId = entry.slice(separator + 1);
        currentIds.add(resourceId);
        // Only open references we haven't already surfaced this mount, so a resync
        // doesn't re-open (and churn) tabs that are already present.
        if (!previousIds.has(resourceId)) {
          artifactPanel.openArtifact({
            type: resourceType,
            artifactId: resourceId,
            payload: resourceArtifactData(resourceType, resourceId),
            focus: false,
          });
        }
      }
    }
    // Close reference tabs whose mention has been removed from the prompt — but only
    // if they're still passive background tabs. `closeBackgroundArtifact` no-ops for
    // tabs the user has engaged with or that a tool/persisted artifact backs, so this
    // never dismisses a tab the expander didn't itself surface.
    for (const id of previousIds) {
      if (!currentIds.has(id)) {
        artifactPanel.closeBackgroundArtifact(id);
      }
    }
    managedArtifactIdsRef.current = currentIds;
  }, [artifactPanel, referenceKey]);

  return null;
};
